import React, { FC, RefObject, useEffect, useRef } from 'react'
import styles from '../TicketLanding.module.css'
import classNames from 'classnames'
import { useInView } from 'react-intersection-observer'
import { QrIcon } from '@/atoms/Icons/QrIcon'
import { CaptionMD } from '@/atoms/Text'
import { EmblaCarousel, EmblaSlide } from '@/molecules/EmblaCarousel'
import { useTranslate } from '@/utils/translate/translate-client'
import { SimpleTicketItem } from '@/views/TicketCheckoutViews/hooks/useSimpleTicketList'

interface Props {
  userTickets: SimpleTicketItem[]
  setUserTicket: (ticket: SimpleTicketItem) => void
  className?: string
}
export const TicketQrCodes: FC<Props> = ({ userTickets, className, setUserTicket }) => {
  const containerRef = useRef<HTMLDivElement | null>(null)

  return (
    <div ref={containerRef} className={classNames('absolute inset-x-0 top-20', className)}>
      <EmblaCarousel
        rightArrowClassname="bg-white right-8 !p-3 top-[72px]"
        leftArrowClassname="bg-white left-8 !p-3 top-[72px]"
        iconColor="black"
        includeArrows
        options={{
          dragFree: true,
          slidesToScroll: 1,
          active: userTickets.length > 1,
        }}
      >
        {userTickets.map((ticket, index) => {
          return (
            <TicketQrCode
              key={ticket?.id}
              setUserTicket={setUserTicket}
              containerRef={containerRef}
              ticket={ticket}
              ticketNumber={index + 1}
              maxTicketNumber={userTickets.length}
            />
          )
        })}
      </EmblaCarousel>
    </div>
  )
}

const TicketQrCode = ({
  ticket,
  ticketNumber,
  maxTicketNumber,
  containerRef,
  setUserTicket,
}: {
  ticket: SimpleTicketItem
  ticketNumber: number
  maxTicketNumber: number
  containerRef: RefObject<HTMLDivElement>
  setUserTicket: (ticket: SimpleTicketItem) => void
}) => {
  const { t } = useTranslate('tickets')
  const { ref, inView } = useInView({ threshold: 0.8, root: containerRef.current })

  const qrCode = ticket?.ticketOrder?.qrCodeSvgXml
  const confirmationCode = ticket?.ticketOrder?.ticketConfirmationCode

  useEffect(() => {
    if (inView) setUserTicket(ticket)
  }, [inView, setUserTicket, ticket])

  if (!qrCode) return null

  return (
    <EmblaSlide fadeOutOfView className="w-[100vw]">
      <div ref={ref} className="flex w-full shrink-0 flex-col items-center">
        <div className={styles.qr} dangerouslySetInnerHTML={{ __html: qrCode }} />
        {confirmationCode && (
          <CaptionMD color="white" className="mt-2 shrink-0 text-center">
            {t('confNumTickets', 'Confirmation')} #{confirmationCode}
          </CaptionMD>
        )}
        <CaptionMD color="core-warning-200" className="mt-2 flex items-center gap-1 text-center">
          <QrIcon size={14} color="core-warning-200" />
          {t('qrCodeAmounts', '{{min}} of {{max}} QR Codes', { min: ticketNumber, max: maxTicketNumber })}
        </CaptionMD>
      </div>
    </EmblaSlide>
  )
}
